import type { Sprache } from '@fh/shared/sprache'

import { useSprache } from './t.js'

/**
 * Zwei Fassungen eines Textes, so untereinander, wie die Regel es verlangt:
 * **was in deiner Sprache steht, steht gross; die andere klein darüber.**
 *
 * Warum das ein eigenes Bauteil ist und keine Zeile im Chat: Dieselbe Regel
 * gilt inzwischen an sechs Stellen — Chatnachricht, Befindlichkeitstext auf der
 * Startseite und im Verlauf, Termintitel, Aufgabentitel, Notiztitel. Sechsmal
 * abgetippt wäre sie fünfmal richtig und einmal andersherum, und niemandem
 * fiele auf, welches Mal.
 *
 * `lang` an beiden Absätzen ist nicht Kosmetik: Eine Vorlesefunktion entscheidet
 * daran, mit welcher Aussprache sie liest. Ohne die Angabe liest ein deutscher
 * Screenreader den englischen Satz deutsch — unverständlicher als gar keiner.
 */
export function Zweisprachig({ text, uebersetzung, klasse, klasseKlein }: {
  /** Der Text, wie er geschrieben wurde. */
  text: string
  /** Die andere Fassung — mit der Sprache des ORIGINALS. */
  uebersetzung?: { sprache: Sprache; text: string } | undefined
  /** Klasse für die grosse Zeile. */
  klasse?: string
  /** Klasse für die kleine. Ohne Angabe die Standardform. */
  klasseKlein?: string
}) {
  const sprache = useSprache()

  // Ohne Übersetzung bleibt es bei einer Zeile. Der häufigste Fall: alle in
  // der Familie schreiben dieselbe Sprache.
  if (!uebersetzung) return <p class={klasse}>{text}</p>

  const original = uebersetzung.sprache
  const andere: Sprache = original === 'de' ? 'en' : 'de'

  const grossIstOriginal = original === sprache
  const gross = grossIstOriginal ? text : uebersetzung.text
  const klein = grossIstOriginal ? uebersetzung.text : text

  return (
    <>
      <p class={klasseKlein ?? 'fh-msg__klein'} lang={grossIstOriginal ? andere : original}>
        {klein}
      </p>
      <p class={klasse} lang={grossIstOriginal ? original : andere}>{gross}</p>
    </>
  )
}

/**
 * Dasselbe für eine Stelle, an der kein Absatz stehen darf — eine Zeile in
 * einer Liste, ein Knopf, eine Kachel.
 *
 * Getrennt gehalten statt über eine Option gesteuert: Wer eine Überschrift
 * baut, soll nicht erst herausfinden müssen, welches Element dabei entsteht.
 */
export function ZweisprachigInline({ text, uebersetzung, klasseKlein }: {
  text: string
  uebersetzung?: { sprache: Sprache; text: string } | undefined
  klasseKlein?: string
}) {
  const sprache = useSprache()
  if (!uebersetzung) return <>{text}</>

  const original = uebersetzung.sprache
  const andere: Sprache = original === 'de' ? 'en' : 'de'
  const grossIstOriginal = original === sprache

  return (
    <>
      <span class={klasseKlein ?? 'fh-zweisprachig__klein'}
        lang={grossIstOriginal ? andere : original}>
        {grossIstOriginal ? uebersetzung.text : text}
      </span>
      <span lang={grossIstOriginal ? original : andere}>
        {grossIstOriginal ? text : uebersetzung.text}
      </span>
    </>
  )
}
